import { useCommunityContext } from 'providers';
import { useQuery } from 'react-query';

import { MarketplaceV1 } from '@jebel/constants';

import { UserKeyFilter } from 'shared/graphql';
import { useHasSession } from 'shared/hooks';

export const USER_AVAILABLE_COMMUNITY_PRODUCTS_QUERY_KEY = 'USER_AVAILABLE_COMMUNITY_PRODUCTS';

/**
 * React hook to fetch a specific user's available community products for the selected community.
 *
 * Uses React Query to asynchronously retrieve community product data from the marketplace API.
 * The query is enabled only if there is an active session and a selected community.
 *
 * @param {UserKeyFilter} user - The user whose available community products should be fetched.
 * @returns The query result containing the available community product data.
 * @throws {Error} If the user or selected community is not available, or if the fetch fails.
 */
export function useUserAvailableCommunityProducts(user: UserKeyFilter | null | undefined) {
  const hasSession = useHasSession();

  const { selectedCommunity } = useCommunityContext();

  return useQuery({
    enabled: Boolean(hasSession && selectedCommunity),
    queryKey: [USER_AVAILABLE_COMMUNITY_PRODUCTS_QUERY_KEY, selectedCommunity?.id],

    async queryFn() {
      if (!user?.id) {
        throw new Error('No current user');
      }

      if (!selectedCommunity?.id) {
        throw new Error('No selected community');
      }

      const url = new URL(
        `/community/${selectedCommunity.id}/product/user/${user.id}`,
        process.env.REACT_APP_MARKETPLACE_API_URL,
      );

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch user community products (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as MarketplaceV1.CommunityProduct[];
    },
  });
}
